// Pure-JS easing implementations. Same numeric output on server and client.

import type { Easing } from "./ir";

// Easing functions tuned for motion-design feel — not just textbook Robert
// Penner. easeOutExpo is the workhorse: cinematic deceleration that ends
// crisp. spring uses underdamped-oscillator physics with a perceptually-
// pleasing ζ for that After-Effects "Inertial" look.

const c1 = 1.70158;
const c3 = c1 + 1;

export function easeFn(name: Easing): (t: number) => number {
  switch (name) {
    case "linear":         return (t) => t;
    case "easeInQuad":     return (t) => t * t;
    case "easeOutQuad":    return (t) => 1 - (1 - t) * (1 - t);
    case "easeInOutQuad":  return (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    case "easeInCubic":    return (t) => t * t * t;
    case "easeOutCubic":   return (t) => 1 - Math.pow(1 - t, 3);
    case "easeInOutCubic": return (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    case "easeInExpo":     return (t) => (t === 0 ? 0 : Math.pow(2, 10 * t - 10));
    case "easeOutExpo":    return (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));
    case "easeInOutExpo":  return (t) =>
      t === 0 ? 0 : t === 1 ? 1 :
      t < 0.5 ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2;
    case "easeOutBack":    return (t) => 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    case "spring":
      // Critically-underdamped, ω≈3.6π, ζ≈0.28 — feels like the AE-Motion
      // preset "Inertia Bounce" rather than a rubber-ball cartoon spring.
      return (t) => {
        if (t <= 0) return 0;
        if (t >= 1) return 1;
        const omega = 3.6 * Math.PI;
        const zeta = 0.28;
        const wd = omega * Math.sqrt(1 - zeta * zeta);
        return 1 - Math.exp(-zeta * omega * t) * (Math.cos(wd * t) + (zeta * omega / wd) * Math.sin(wd * t));
      };
  }
}
